import { fromEvent, merge, Observable } from 'rxjs';
import { filter, map, mergeMap, startWith } from 'rxjs/operators';

enum SCROLL_DIRECTIONS {
  UP = 'up',
  DOWN = 'down',
  LEFT = 'left',
  RIGHT = 'right',
  UNDEFINED = 'undefined',
}

interface ScrollPosition {
  x: number | undefined;
  y: number | undefined;
  direction?: SCROLL_DIRECTIONS;
}

const Events = {
  WHEEL: 'wheel',
  SCROLL: 'scroll',
};

class ScrollService {
  private cache: ScrollPosition = {
    x: undefined,
    y: undefined,
    direction: SCROLL_DIRECTIONS.UNDEFINED,
  };

  public scrollEvent: Observable<ScrollPosition>;

  /**
   *
   */
  constructor() {
    this.scrollEvent = this.registerScrollEventStream();
  }

  public registerScrollEventStream(): Observable<ScrollPosition> {
    const wheelStream = fromEvent(window, Events.WHEEL);
    const scrollStream = fromEvent(window, Events.SCROLL);

    return merge(wheelStream, scrollStream)
      .pipe(startWith(this.getWindowScroll()))
      .pipe(mergeMap(() => this.getWindowScroll()))
      .pipe(filter((data) => this.didChange(data)))
      .pipe(map((data) => this.cacheAndReturn(data)));
  }

  public toPosition(data: ScrollPosition, options = { animated: false }): void {
    window.scrollTo({
      left: data.x,
      top: data.y,
      behavior: options.animated ? 'smooth' : 'auto',
    });
  }

  public async getCurrent(): Promise<ScrollPosition> {
    if (this.cache.x !== undefined && this.cache.y !== undefined) {
      return this.cache;
    }
    return this.getWindowScroll();
  }

  public async toElement(
    element: HTMLElement,
    ensureScrollDown = false,
    scrollDownOffset = -25
  ): Promise<void> {
    let pos1 = 0;
    let pos2 = 0;

    if (ensureScrollDown) {
      pos2 = this.getOffset(element).top;
      pos1 = pos2 + scrollDownOffset;

      await this.scrollToPosition(pos1, 600);
      await this.scrollToPosition(pos2, 300);

    } else {
      // DOM
      this.scrollToPosition(element.offsetTop, 600);
    }
  }

  private getOffset(element: HTMLElement) {
    if (!element.getClientRects().length) {
      return { top: 0, left: 0 };
    }

    let rect = element.getBoundingClientRect();
    let win = element.ownerDocument.defaultView;
    return {
      top: rect.top + win.pageYOffset,
      left: rect.left + win.pageXOffset,
    };
  }

  private async scrollToPosition(positionTop: number, duration: number): Promise<void> {

    return new Promise((resolve) => {
      const start = performance.now();
      const from = document.documentElement.scrollTop || document.body.scrollTop;
  
      function scrollStep(timestamp: number) {
        const currentTime = timestamp - start;
        const progress = Math.min(currentTime / duration, 1);
        const easeInOutCubic = (t: number) =>
          t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
        const to = positionTop; // Scroll to the target
  
        document.documentElement.scrollTop = document.body.scrollTop =
          from + (to - from) * easeInOutCubic(progress);
  
        if (currentTime < duration) {
          requestAnimationFrame(scrollStep);
        } else {
          resolve();
        }
      }
      requestAnimationFrame(scrollStep);
    });
  }

  private cacheAndReturn(data: ScrollPosition): ScrollPosition {
    this.cache = data;
    return this.cache;
  }

  private didChange(data: ScrollPosition): boolean {
    return !(
      data.x === this.cache.x &&
      data.y === this.cache.y &&
      data.direction === this.cache.direction
    );
  }

  private determineDirection(data: ScrollPosition): SCROLL_DIRECTIONS {
    const oldX = this.cache.x;
    const oldY = this.cache.y;
    const newX = data.x;
    const newY = data.y;

    if (oldX !== undefined && oldY !== undefined) {
      return newY > oldY
        ? SCROLL_DIRECTIONS.DOWN
        : newY < oldY
        ? SCROLL_DIRECTIONS.UP
        : newX > oldX
        ? SCROLL_DIRECTIONS.LEFT
        : newX < oldX
        ? SCROLL_DIRECTIONS.RIGHT
        : this.cache.direction;
    } else {
      return SCROLL_DIRECTIONS.UNDEFINED;
    }
  }

  private async getWindowScroll(): Promise<ScrollPosition> {
    const scrollX =
      window.pageXOffset !== undefined
        ? window.pageXOffset
        : document.documentElement?.scrollLeft;
    const scrollY =
      window.pageYOffset !== undefined
        ? window.pageYOffset
        : document.documentElement?.scrollTop;

    const normalizedX = scrollX !== undefined ? Math.floor(scrollX) : 0;
    const normalizedY = scrollY !== undefined ? Math.floor(scrollY) : 0;

    return {
      x: normalizedX,
      y: normalizedY,
      direction: this.determineDirection({ x: normalizedX, y: normalizedY }),
    };
  }
}

export { SCROLL_DIRECTIONS as DIRECTIONS, ScrollPosition };
export default new ScrollService();
